import { faChevronDown, faChevronUp, faStar, faUser } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StatusCode } from "api/protocols";
import { GetLoyaltyProgramRewards, GetLoyaltyProgramTiers } from "api/rpc/2024-04/facilityAdmin/customer/loyalty";
import axios, { CancelToken } from "axios";
import classNames from "classnames";
import { Badge } from "components/badge/Badge";
import Checkbox from "components/form/checkbox/Checkbox";
import Sheet from "components/sheet/Sheet";
import Spin from "components/spin/spin";
import Portal from "elements/Portal";
import { LocaleCurrency } from "helpers/Locale";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { ILoyaltyCustomerExtended } from "pages/secure/facility/settings/loyaltyProgram/loyaltyTabs/LoyaltyCustomers";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { showError } from "redux/actions/ui";
import { ICartLineItem, IDiscountLines } from "redux/reducers/models/cart";
import { IReward, ITier } from "redux/reducers/models/loyalty";
import { IProduct, TProductType } from "redux/reducers/models/product";

interface IProps {
    open: boolean;
    customer: ILoyaltyCustomerExtended & {
        points_available?: number;
    };
    onCancel: () => void;
    onOk: (rewards: Array<{reward_id: number; cart_line_item_id: number}>) => void;
    lineItems: Array<ICartLineItem>;
    discountLines: Array<IDiscountLines>
}

interface IRewardExtended extends IReward {
    products?: Array<IProduct>; 
    product_types?: Array<TProductType>;
    open?: boolean; 
    lineItems?: Array<ICartLineItem>
    tier: ITier;
}

interface IState {
    rewards: Array<IRewardExtended>;
    selectedRewards: Array<{reward_id: number; line_item_id: number; product_title: string; variant_title: string; points: number}>;
}

export default function RedeemTierPointsModal(props: IProps) {
    const {open, onCancel, customer, lineItems, discountLines, onOk} = props;
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const {facilityStore} = useAppSelector(store => store);

    const [state, setState] = useState<IState>({
        rewards: undefined,
        selectedRewards: [],
    });
    const [pointsToUse, setPointsToUse] = useState<number>(0);


    useEffect(() => {
        const source = axios.CancelToken.source();
        if (open) {
            void getRewards(source.token);
        } else {
            setState(prev => ({...prev, rewards: undefined, selectedRewards: []}));
        }
        return () => source.cancel();
    }, [open]);

    useEffect(() => {
        const totalPoints = state?.selectedRewards?.reduce((total, curr) => total + curr?.points, 0);
        setPointsToUse(totalPoints);
    }, [state?.selectedRewards]);

    async function getRewards(cancelToken: CancelToken) {
        if (state?.rewards !== undefined) {
            setState(prev => ({...prev, rewards: undefined}));
        }
        const [rewardsRes, tiersRes] = await Promise.all([GetLoyaltyProgramRewards(false, cancelToken), GetLoyaltyProgramTiers(false, cancelToken)]);
        if (rewardsRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting rewards")));
            return;
        }

        if (tiersRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting tiers")));
            return;
        }
        const rewards = rewardsRes?.data?.filter(reward => !reward?.cash_back );
        const tiers: Array<ITier> = tiersRes?.data;

        const updatedRewards = rewards?.reduce((rewardsLineItems, reward) => {
            const foundTier = tiers?.find(tier => tier?.id === reward?.loyalty_program_tier_id);
            if (!foundTier) {
                //Reward not linked to tier
                return rewardsLineItems;
            }
            if (customer?.points_balance < foundTier?.points_required) {
                //Customer has not reached tier requirements
                return rewardsLineItems;
            }
            
            if (reward?.application === "product") {
                const filteredLineItems = lineItems?.filter(lineItem => reward?.products?.some(product => product?.id === lineItem?.product_id));
                //No eligible rewards, don't display reward
                if (filteredLineItems?.length === 0) {
                    return rewardsLineItems;
                }
                return [...rewardsLineItems, {...reward, lineItems: filteredLineItems, open: false, tier: foundTier}];
            } else if (reward?.application === "product_categories") {
                const filteredLineItems = lineItems?.filter(lineItem => reward?.product_types?.some(type => type?.title === lineItem?.product?.type));
                //No eligible rewards, don't display reward
                if (filteredLineItems?.length === 0) {
                    return rewardsLineItems;
                }
                return [...rewardsLineItems, {...reward, lineItems: filteredLineItems, open: false, tier: foundTier}];
            }
            
            return [...rewardsLineItems, {...reward, lineItems: [], open: false, tier: foundTier}];
        } ,[] as Array<IRewardExtended>);

        let selectedLineItems: Array<Record<string, any> & {reward_id?: number; points_required?: number}> = [];

        //Pre-select the items that already have rewards applied to them
        updatedRewards?.forEach((reward, index) => {
            const filteredLineItems = reward?.lineItems?.filter(item =>
                 discountLines?.some(discount => discount?.cart_line_item_id === item?.id && discount?.loyalty_points_reward_id === reward?.id));
            if (filteredLineItems?.length > 0) {
                selectedLineItems = [...selectedLineItems, ...filteredLineItems.map(item => ({...item, reward_id: reward?.id, points_required: reward?.points_required}))];
                //Auto open the rewards that have items pre-selected
                updatedRewards[index] = {...updatedRewards[index], open: true};
            }
            if (reward?.application === "order" && discountLines?.some(discount => !discount?.cart_line_item_id && discount?.loyalty_points_reward_id === reward?.id)) {
                selectedLineItems = [...selectedLineItems, 
                    {
                    reward_id: reward?.id, 
                    points_required: reward?.points_required,
                    id: null,
                    preferred_title: "",
                    variant_title: "",
                }];
            }
        });

        //Setup format of selected rewards
        let selectedRewards: typeof state.selectedRewards = [];
        if (selectedLineItems?.length > 0 ){ 
            selectedRewards = selectedLineItems?.map(lineItem => ({
                reward_id: lineItem?.reward_id, 
                line_item_id: lineItem?.id, 
                product_title: lineItem?.preferred_title ? lineItem?.preferred_title : lineItem?.product?.title, 
                variant_title: lineItem?.variant_title !== lineItem?.product?.title ? lineItem?.variant_title : "",
                points: lineItem?.points_required,
            }));
        }

        //Show lower tier rewards first
        updatedRewards?.sort((a, b) => a?.tier?.points_required - b?.tier?.points_required);
        setState(prev => ({...prev, rewards: updatedRewards, selectedRewards: selectedRewards}));
    }

    function handleSelectReward(rewardId: number, points: number, lineItem?: ICartLineItem) {
        let updatedSelection = [...state.selectedRewards];
        const foundReward = updatedSelection?.findIndex(reward => lineItem ? lineItem?.id === reward?.line_item_id : rewardId === reward?.reward_id);
        if (foundReward !== -1) {
            updatedSelection = updatedSelection?.filter(reward => lineItem ? lineItem?.id !== reward?.line_item_id : rewardId !== reward?.reward_id);
        } else {
            //Order discount selected, remove entire selection and only add the selected reward
            if (!lineItem) {
                updatedSelection = [];
            } else if (updatedSelection?.some(item => !item?.line_item_id) && lineItem) {
                //Selection contains an order discount and a product discount was selected
                //Remove the order discount
                updatedSelection = updatedSelection?.filter(item => item?.line_item_id);
            }

            updatedSelection = [...updatedSelection, {
                reward_id: rewardId, 
                line_item_id: lineItem?.id, 
                product_title: lineItem?.preferred_title ? lineItem?.preferred_title : lineItem?.product?.title, 
                variant_title: lineItem?.variant_title !== lineItem?.product?.title ? lineItem?.variant_title : "",
                points: points,
            }];
        }
        setState(prev => ({...prev, selectedRewards: updatedSelection}));
    }

    function handleSelectAllRewards(reward: IRewardExtended) {
        let updatedSelection = [...state.selectedRewards];
        const allSelected = reward?.lineItems?.every(item => updatedSelection?.some(selectedReward => selectedReward?.line_item_id === item?.id && reward?.id === selectedReward?.reward_id));
        let pointsUsed = pointsToUse;
        if (allSelected) {
            updatedSelection = updatedSelection?.filter(item => item?.reward_id !== reward?.id);
        } else {
            //Selection contains an order discount and a product discount was selected
            if (updatedSelection?.some(item => !item?.line_item_id)) {
                //Remove the order discount
                updatedSelection = updatedSelection?.filter(item => {
                    if (item?.line_item_id) {
                        return true;
                    } else {
                        pointsUsed = pointsUsed - item?.points;
                        return false;
                    }
                });
            }
            // Add missing items
            reward?.lineItems?.forEach(item => {
                if (!updatedSelection?.some(foundItem => foundItem?.line_item_id === item?.id)) {
                    if (!(((pointsUsed + reward?.points_required) > customer?.points_balance) || (customer?.points_balance - pointsUsed < reward?.points_required))) {
                        updatedSelection = [...updatedSelection, {
                            reward_id: reward?.id, 
                            line_item_id: item?.id, 
                            product_title: item?.preferred_title ? item?.preferred_title : item?.product?.title, 
                            variant_title: item?.variant_title !== item?.product?.title ? item?.variant_title : "",
                            points: reward?.points_required,
                        },
                        ];
                        pointsUsed = pointsUsed + reward?.points_required;
                    }
                }
            });
        }
        setState(prev => ({...prev, selectedRewards: updatedSelection}));
    }

    function handleOk() {
        const rewardLineItems = state?.selectedRewards?.map(reward => ({reward_id: reward?.reward_id, cart_line_item_id: reward?.line_item_id}));
        void onOk(rewardLineItems);
    }

    return (
        <Portal
        isMounted={open}
        >
            <Sheet
                title={t("Redeem Points")}
                supportText={t("Select eligible items to pay with points")}
                open={open}
                onCancel={() => onCancel()}
                onOk={handleOk}
                size="small"
                okText={t("Continue")}
                secondaryFooter={
                    <p className="redeem-points-modal-rewards-title">{t("Selected")}: {state?.selectedRewards?.length} {t("items")} • {pointsToUse} {t("pts ")} 
                    — {t(" Remaining:")} {customer?.points_balance - pointsToUse} / {customer?.points_balance}
                    </p>
                }
            >
                <div className="redeem-points-modal-customer-container">
                    <div className="redeem-points-modal-customer-icon">
                        <FontAwesomeIcon icon={faUser} />
                    </div>

                    <div>
                        <p className="redeem-points-modal-rewards-title">{customer?.customer_first_name} {customer?.customer_last_name}</p>
                        <p className="redeem-points-modal-subtitle">{t("Tier: ")} {customer?.selected_tier?.title ?? t("No tier reached")} • {customer?.points_balance} {t("pts")}</p>
                    </div>
                </div>
                {state?.rewards === undefined ? (<div style={{width: "100%", height: "30px", display: "flex", justifyContent: "center"}}><Spin/></div>) : (
                        <>
                        {state?.rewards?.map((reward, index) => {
                        const hasRewards = reward?.product_types?.length > 0 || reward?.products?.length > 0;
                        const amountOfRewards = reward?.lineItems?.length;
                        const validRewards = amountOfRewards > 0;
                        if (reward?.application === "product" || reward?.application === "product_categories") {
                            if (validRewards) {
                            return (
                                //Reward w/ products or product types
                                <div key={index}>
                                <div className="redeem-points-modal-reward-container" 
                                onClick={hasRewards ? () => setState(prev => ({...prev, rewards: prev?.rewards?.map(prevReward => prevReward?.id === reward?.id ? {...prevReward, open: !prevReward?.open} : prevReward)})) : undefined}>
                                    <div style={{display: "flex", gap: "1rem"}}>
                                    <div
                                    onClick={e => e.stopPropagation()}
                                    >
                                    <Checkbox labelHidden size="medium" 
                                        disabled={(((pointsToUse + reward?.points_required) > customer?.points_balance) || (customer?.points_balance - pointsToUse < reward?.points_required)) 
                                        && !state?.selectedRewards?.some(item => item?.reward_id === reward?.id)}
                                        checked={reward?.lineItems?.every(item => state?.selectedRewards?.some(selectedReward => selectedReward?.line_item_id === item?.id && reward?.id === selectedReward?.reward_id))} 
                                        onChange={() => handleSelectAllRewards(reward)}/>
                                    </div>
                                    <div>
                                    <p className="redeem-points-modal-rewards-title">{reward?.name} <span className="ml-2">

                                        {hasRewards ? reward?.application === "product" ? <Badge type="gray" size="small">{amountOfRewards}</Badge> : reward?.application === "product_categories" ? 
                                        <Badge type="gray" size="small">{amountOfRewards}</Badge> : null : null}</span></p>
                                         <p className="redeem-points-modal-subtitle">{reward?.points_required} {t("points")} = {reward?.reward_type === "discount_amount" ? 
                                        <LocaleCurrency currency={facilityStore?.facility?.default_currency_code} amount={reward?.discount_value}/> : 
                                        `${reward?.discount_value}%`
                                        } {t("off")}</p>
                                         <p className="redeem-points-modal-subtitle redeem-points-modal-selected-product-container">{state?.selectedRewards?.filter(selectedReward => selectedReward?.reward_id === reward?.id)?.map((selectedReward, selectedIndex) => (
                                        <span key={selectedIndex} className="redeem-points-modal-selected-product">{selectedReward?.product_title} {selectedReward?.variant_title ? ` - ${selectedReward?.variant_title}` : null} </span>
                                    ))}</p>
                                    </div>
                                    </div>
                                    
                                    <div>
                                    <Badge type="gray" size="medium" className="mr-2">{reward?.tier?.title}</Badge>
                                    {hasRewards ? (
                                        <FontAwesomeIcon icon={reward?.open === false ? faChevronDown : faChevronUp} />
                                    ) : null}
                                    </div>
                                    
                                </div>
                                    {reward?.open ? (
                                        <div className="redeem-points-modal-product-outer">
                                        {reward?.application === "product" ? reward?.lineItems?.map((lineItem, lineItemIndex) => {
                                            const checked = state?.selectedRewards?.some(foundReward => foundReward?.line_item_id === lineItem?.id && foundReward?.reward_id === reward?.id);
                                            const disabled = state?.selectedRewards?.some(foundReward => foundReward?.line_item_id === lineItem?.id && foundReward?.reward_id !== reward?.id) 
                                            || ((((pointsToUse + reward?.points_required) > customer?.points_balance) || (customer?.points_balance - pointsToUse < reward?.points_required)) && !checked);
                                            //Specific product rewards
                                            return (
                                                <div className={classNames("redeem-points-modal-product-container", {
                                                    "redeem-points-modal-product-container-disabled": disabled,
                                                })} key={lineItemIndex} onClick={!disabled ? () => handleSelectReward(reward?.id, reward?.points_required, lineItem) : undefined}>
                                                <div style={{display: "flex", gap: "1rem"}}>
                                                    <div className="pointer-events-none">
                                                        <Checkbox 
                                                        labelHidden 
                                                        size="medium" 
                                                        checked={checked} 
                                                        disabled={disabled}
                                                        onChange={() => handleSelectReward(reward?.id, reward?.points_required, lineItem)}/>
                                                    </div>
                                                    <div>
                                                    <p className="redeem-points-modal-rewards-title">{lineItem?.preferred_title ? lineItem?.preferred_title : lineItem?.product?.title}</p>
                                                    {lineItem?.product?.title !== lineItem?.variant_title ? (
                                                        <p className="redeem-points-modal-subtitle">{lineItem?.variant_title}</p>
                                                    ) : null}
                                                    </div>
                                                </div>
                                                <Badge size="small" colour="#363F72" backgroundColour="#F8F9FC">{reward?.points_required} {t("pts")} <FontAwesomeIcon icon={faStar}/></Badge>
                                            </div>
                                            );
                                        }) : 
                                        reward?.lineItems?.map((lineItem, lineItemIndex) => {
                                            const checked = state?.selectedRewards?.some(foundReward => foundReward?.line_item_id === lineItem?.id && foundReward?.reward_id === reward?.id);
                                            const disabled = state?.selectedRewards?.some(foundReward => foundReward?.line_item_id === lineItem?.id && foundReward?.reward_id !== reward?.id) 
                                            || ((((pointsToUse + reward?.points_required) > customer?.points_balance) || (customer?.points_balance - pointsToUse < reward?.points_required)) && !checked);
                                            //Product categories reward
                                            return (
                                                <div className={classNames("redeem-points-modal-product-container", {
                                                    "redeem-points-modal-product-container-disabled": disabled,
                                                })} key={lineItemIndex} onClick={!disabled ? () => handleSelectReward(reward?.id, reward?.points_required, lineItem) : undefined}>
                                                <div style={{display: "flex", gap: "1rem"}}>
                                                    <div className="pointer-events-none">
                                                        <Checkbox 
                                                        labelHidden 
                                                        size="medium" 
                                                        checked={checked} 
                                                        disabled={disabled}
                                                        onChange={() => handleSelectReward(reward?.id, reward?.points_required, lineItem)}/>
                                                    </div>
                                                    <div>
                                                    <p className="redeem-points-modal-rewards-title">{lineItem?.preferred_title ? lineItem?.preferred_title : lineItem?.product?.title}</p>
                                                    {lineItem?.product?.title !== lineItem?.variant_title ? (
                                                        <p className="redeem-points-modal-subtitle">{lineItem?.variant_title}</p>
                                                    ) : null}
                                                    </div>
                                                </div>
                                                <Badge size="small" colour="#363F72" backgroundColour="#F8F9FC">{reward?.points_required} {t("pts")} <FontAwesomeIcon icon={faStar}/></Badge>
                                            </div>
                                            );
                                        })
                                        }
                                        </div>
                                    ) : null}
                                </div>
                            );
                        }
                        } else {
                            const checked = state?.selectedRewards?.some(foundReward => foundReward?.reward_id === reward?.id);
                            const disabled = reward?.points_required > customer?.points_balance && !checked;
                            //Order subtotal reward
                            return (
                                <div key={index} className="redeem-points-modal-reward-container" onClick={!disabled ? () => handleSelectReward(reward?.id, reward?.points_required) : undefined}>
                                    <div style={{display: "flex", gap: "1rem"}}>
                                        <div className="pointer-events-none">
                                            <Checkbox disabled={disabled} labelHidden size="medium" checked={checked} onChange={() => handleSelectReward(reward?.id, reward?.points_required)}/>
                                        </div>
                                        <div>
                                    <p className="redeem-points-modal-rewards-title">{reward?.name}</p>
                                    <p className="redeem-points-modal-subtitle">{reward?.points_required} {t("points")} = {reward?.reward_type === "discount_amount" ? 
                                        <LocaleCurrency currency={facilityStore?.facility?.default_currency_code} amount={reward?.discount_value}/> : 
                                        `${reward?.discount_value}%`
                                        } {t("off subtotal")}</p>
                                        </div>
                                    </div>
                                    <Badge size="small" colour="#363F72" backgroundColour="#F8F9FC">{reward?.points_required} {t("pts")} <FontAwesomeIcon icon={faStar}/></Badge>
                                </div>
                            );
                        }
                    })}
                        </>
                    )}
            </Sheet>
        </Portal>
    );
}